import React, { useLayoutEffect, useRef } from "react";

import {
  useLeafFlagSaves,
  useLeafSaveStatus,
  useLeafSavedValue,
} from "@/hooks/useLeafFlagSaves";

import { cn } from "@/lib/utils";

import type { Leaf } from "./LeafRow";

const grow = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

export const LeafHelpPanel: React.FC<{
  leaf: Leaf;
  id: string;
  onClose: () => void;
}> = ({ leaf, id, onClose }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const { save } = useLeafFlagSaves();
  const status = useLeafSaveStatus(leaf.path, "helpText");
  const tried = useLeafSavedValue(leaf.path, "helpText");
  useLayoutEffect(() => {
    grow(ref.current);
    ref.current?.focus();
  }, []);
  const commit = (value: string) =>
    (value !== (leaf.helpText ?? "") || status === "failed") &&
    save(leaf, { helpText: value }).catch(() => {});

  return (
    <div
      id={id}
      className="fade-in grid gap-4 border-t border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-[#0B1222] min-[1400px]:grid-cols-[1fr_300px]"
    >
      <label className="block">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
          Help text the seller sees under this box
        </span>
        <textarea
          ref={ref}
          rows={2}
          defaultValue={
            typeof tried === "string" ? tried : (leaf.helpText ?? "")
          }
          aria-label={`Help text: ${leaf.leaf}`}
          onInput={(e) => grow(e.currentTarget)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape" && !e.nativeEvent.isComposing) onClose();
          }}
          className={cn(
            "mt-1 block w-full resize-none overflow-hidden rounded-md border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed text-slate-900 transition-colors duration-150 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500",
            status === "failed" &&
              "border-rose-400 ring-2 ring-rose-400/20 dark:border-rose-400",
          )}
        />
        <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
          Esc closes the box. The text saves when it closes.
        </span>
      </label>
      <div>
        <p className="text-xs font-medium text-slate-600 dark:text-slate-300">
          What this leaf means
        </p>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          {leaf.description}
        </p>
      </div>
    </div>
  );
};
